import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { authApi, authKeys, useAuthState, type TotpReset } from '@/api/auth'
import { ApiError } from '@/api/client'
import { useLogout } from '@/api/useLogout'
import { Button, Card, ErrorText, Input, Modal, useToast } from '@/components/ui'
import { pluralRu } from '@/lib/format'
import { RecoveryCodesList, TotpCodeInput, TotpQr } from '@/pages/auth/parts'
import { digitsOnly } from '@/pages/auth/utils'

const MIN_PASSWORD = 8

function PasswordCard() {
  const toast = useToast()
  const auth = useAuthState()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [touched, setTouched] = useState(false)

  const change = useMutation({
    mutationFn: () => authApi.changePassword(current, next),
    onSuccess: () => {
      setCurrent('')
      setNext('')
      setRepeat('')
      setTouched(false)
      toast.show({ message: 'Пароль изменён. Другие устройства вышли из сервиса.', tone: 'accent' })
    },
  })

  const tooShort = next.length < MIN_PASSWORD
  const mismatch = next !== repeat
  const currentError = change.error instanceof ApiError && change.error.field === 'current' ? change.error.message : null
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (current && !tooShort && !mismatch) change.mutate()
  }

  return (
    <Card title="Пароль" subtitle="После смены пароля все остальные сессии закрываются.">
      <form onSubmit={submit} className="grid gap-4 sm:max-w-sm" noValidate>
        <input type="text" name="username" autoComplete="username" value={auth.data?.user?.email ?? ''} readOnly hidden />
        <Input
          label="Текущий пароль"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          error={currentError}
        />
        <Input
          label="Новый пароль"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          error={touched && tooShort ? `Не короче ${MIN_PASSWORD} символов` : null}
        />
        <Input
          label="Повтори новый пароль"
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
          error={touched && !tooShort && mismatch ? 'Пароли не совпадают' : null}
        />
        {change.error && !currentError ? (
          <ErrorText error={change.error} />
        ) : null}
        <div>
          <Button type="submit" variant="primary" loading={change.isPending}>
            Сменить пароль
          </Button>
        </div>
      </form>
    </Card>
  )
}

/** Подтверждение паролем перед чувствительным действием. */
function PasswordPrompt({
  pending,
  error,
  onSubmit,
  submitLabel,
}: {
  pending: boolean
  error: unknown
  onSubmit: (password: string) => void
  submitLabel: string
}) {
  const [password, setPassword] = useState('')
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (password) onSubmit(password)
      }}
      noValidate
    >
      <Input
        label="Текущий пароль"
        type="password"
        autoComplete="current-password"
        autoFocus
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <ErrorText error={error} />
      <Button type="submit" variant="primary" loading={pending} disabled={!password}>
        {submitLabel}
      </Button>
    </form>
  )
}

function TotpCard() {
  const toast = useToast()
  const client = useQueryClient()
  const auth = useAuthState()
  const security = useQuery({ queryKey: authKeys.security, queryFn: authApi.security })
  const enabled = security.data?.totp_enabled ?? auth.data?.user?.totp_enabled
  const canDisable = enabled === true && auth.data?.user?.role === 'user'
  const [open, setOpen] = useState(false)
  const [disableOpen, setDisableOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [factor, setFactor] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const disabling = useRef(false)
  const [enrollment, setEnrollment] = useState<TotpReset | null>(null)
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

  const start = useMutation({
    mutationFn: (password: string) => authApi.resetTotp(password),
    onSuccess: setEnrollment,
  })
  const confirm = useMutation({
    mutationFn: (value: string) => authApi.confirmTotp(enrollment?.pending ?? '', value),
    onSuccess: (data) => {
      setRecoveryCodes(data.recovery_codes)
      setEnrollment(null)
      void client.invalidateQueries({ queryKey: authKeys.security })
      void client.invalidateQueries({ queryKey: authKeys.state })
      toast.show({ message: enabled ? '2FA перевыпущена. Старый код больше не работает.' : '2FA включена.', tone: 'accent' })
    },
    onError: () => setCode(''),
  })
  const disable = useMutation({
    mutationFn: () => authApi.disableTotp({ password, ...(useRecovery ? { recovery_code: factor.trim() } : { code: digitsOnly(factor) }) }),
    onSuccess: () => {
      setDisableOpen(false); setPassword(''); setFactor('')
      client.setQueryData(authKeys.security, { totp_enabled: false, recovery_codes_left: 0 })
      void client.invalidateQueries({ queryKey: authKeys.security })
      void client.invalidateQueries({ queryKey: authKeys.state })
      toast.show({ message: '2FA отключена.', tone: 'accent' })
    },
  })
  const closeDisable = () => {
    if (disabling.current) return
    setDisableOpen(false); setPassword(''); setFactor(''); setUseRecovery(false); disable.reset()
  }

  function close() {
    setOpen(false)
    setEnrollment(null)
    setRecoveryCodes(null)
    setCode('')
    start.reset()
    confirm.reset()
  }

  return (
    <Card
      title="Двухфакторная аутентификация"
      subtitle={enabled ? 'Код из приложения-аутентификатора нужен при каждом входе.' : '2FA выключена. Можно добавить код из приложения к паролю.'}
    >
      <p className="text-sm text-muted">
        {enabled ? 'Сменил телефон или боишься, что ключ утёк, — перевыпусти 2FA. Пока новый код не подтверждён, работает прежний.' : 'Отсканируй QR-код и подтверди код из приложения, чтобы включить 2FA.'}
        {auth.data?.user?.role === 'admin' ? ' Администратору 2FA обязательна.' : ''}
      </p>
      <Button className="mt-3" onClick={() => setOpen(true)}>
        {enabled ? 'Перевыпустить 2FA' : 'Включить 2FA'}
      </Button>
      {canDisable ? <Button className="mt-3 sm:ml-2" variant="danger" onClick={() => setDisableOpen(true)}>Отключить 2FA</Button> : null}
      <Modal open={disableOpen && canDisable} onClose={closeDisable} title="Отключить 2FA?" description="Подтверди паролем и текущим кодом из приложения или резервным кодом.">
        <form className="grid gap-4" onSubmit={(event) => {
          event.preventDefault()
          if (disabling.current || !password || (useRecovery ? !factor.trim() : digitsOnly(factor).length !== 6)) return
          disabling.current = true
          void disable.mutateAsync().catch(() => undefined).finally(() => { disabling.current = false })
        }}>
          <Input label="Текущий пароль" type="password" autoComplete="current-password" autoFocus value={password} onChange={(event) => setPassword(event.target.value)} />
          {useRecovery ? <Input label="Резервный код" autoComplete="one-time-code" value={factor} onChange={(event) => setFactor(event.target.value)} /> : <TotpCodeInput label="Код из приложения" value={factor} onChange={(event) => setFactor(event.target.value)} />}
          <Button variant="ghost" onClick={() => { setUseRecovery(!useRecovery); setFactor('') }} disabled={disable.isPending}>{useRecovery ? 'Использовать код из приложения' : 'Использовать резервный код'}</Button>
          <ErrorText error={disable.error} />
          <div className="flex gap-2"><Button onClick={closeDisable} disabled={disable.isPending}>Отмена</Button><Button type="submit" variant="danger" loading={disable.isPending} disabled={!password || !factor.trim()}>Отключить</Button></div>
        </form>
      </Modal>
      <Modal
        open={open}
        onClose={close}
        title={enabled ? 'Перевыпуск 2FA' : 'Включение 2FA'}
        description={recoveryCodes ? 'Сохрани новые коды восстановления: они показаны один раз.' : enrollment ? 'Отсканируй новый QR-код и введи код из приложения.' : 'Подтверди паролем.'}
        className="sm:max-w-lg"
      >
        {recoveryCodes ? <div><RecoveryCodesList codes={recoveryCodes} /><Button className="mt-4" variant="primary" block onClick={close}>Готово</Button></div> : enrollment ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              const value = digitsOnly(code)
              if (value.length === 6) confirm.mutate(value)
            }}
            noValidate
          >
            <TotpQr uri={enrollment.totp_uri} secret={enrollment.totp_secret_display} />
            <TotpCodeInput
              label="Код из приложения"
              autoFocus
              value={code}
              onChange={(e) => {
                setCode(e.target.value)
                const value = digitsOnly(e.target.value)
                if (value.length === 6 && !confirm.isPending) confirm.mutate(value)
              }}
            />
            <ErrorText error={confirm.error} />
            <Button type="submit" variant="primary" loading={confirm.isPending} disabled={digitsOnly(code).length !== 6}>
              Подтвердить
            </Button>
          </form>
        ) : (
          <PasswordPrompt
            pending={start.isPending}
            error={start.error}
            onSubmit={(password) => start.mutate(password)}
            submitLabel="Дальше"
          />
        )}
      </Modal>
    </Card>
  )
}

function RecoveryCard() {
  const queryClient = useQueryClient()
  const info = useQuery({ queryKey: authKeys.security, queryFn: authApi.security })
  const [open, setOpen] = useState(false)
  const [codes, setCodes] = useState<string[] | null>(null)

  const regenerate = useMutation({
    mutationFn: (password: string) => authApi.regenerateRecoveryCodes(password),
    onSuccess: (data) => {
      setCodes(data.recovery_codes)
      void queryClient.invalidateQueries({ queryKey: authKeys.security })
    },
  })

  const close = () => {
    setOpen(false)
    setCodes(null)
    regenerate.reset()
  }

  const left = info.data?.recovery_codes_left
  return (
    <Card title="Коды восстановления" subtitle="Для входа без телефона: каждый код работает один раз.">
      <p className="text-sm text-muted">
        {left === undefined ? (
          'Загружаю…'
        ) : (
          <>
            Осталось <span className="font-semibold tabular-nums text-fg">{left}</span>{' '}
            {pluralRu(left, ['код', 'кода', 'кодов'])} из 10.
            {left <= 3 ? ' Пора выпустить новые.' : ''}
          </>
        )}
      </p>
      <Button className="mt-3" onClick={() => setOpen(true)}>
        Выпустить новые коды
      </Button>
      <Modal
        open={open}
        onClose={close}
        title="Новые коды восстановления"
        description={
          codes
            ? 'Старые коды больше не работают. Сохрани новые — они показаны один раз.'
            : 'Старые коды перестанут работать. Подтверди паролем.'
        }
        footer={
          codes ? (
            <Button variant="primary" onClick={close}>
              Я сохранил коды
            </Button>
          ) : null
        }
      >
        {codes ? (
          <RecoveryCodesList codes={codes} />
        ) : (
          <PasswordPrompt
            pending={regenerate.isPending}
            error={regenerate.error}
            onSubmit={(password) => regenerate.mutate(password)}
            submitLabel="Выпустить"
          />
        )}
      </Modal>
    </Card>
  )
}

function SessionsCard() {
  const [open, setOpen] = useState(false)
  const logoutAll = useLogout(true)
  return (
    <Card title="Сессии" subtitle="Вход действует 30 дней на каждом устройстве.">
      <p className="text-sm text-muted">
        Потерял телефон или входил с чужого компьютера — выйди на всех устройствах, включая это.
      </p>
      <Button variant="danger" className="mt-3" onClick={() => setOpen(true)}>
        Выйти на всех устройствах
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Выйти на всех устройствах?"
        description="Все сессии закроются, войти заново можно паролем и кодом 2FA."
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Отмена</Button>
            <Button variant="danger" loading={logoutAll.isPending} onClick={() => logoutAll.mutate()}>
              Выйти везде
            </Button>
          </>
        }
      />
    </Card>
  )
}

export function SecurityPage() {
  const auth = useAuthState()
  return (
    <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
      <PasswordCard />
      <div className="grid gap-4">
        <TotpCard />
        <RecoveryCard />
        <SessionsCard />
        <p className="px-1 text-sm text-muted">
          Не помнишь пароль?{' '}
          <Link className="text-accent underline underline-offset-4" to={`/forgot-password?email=${encodeURIComponent(auth.data?.user?.email ?? '')}`}>
            Запроси ссылку для восстановления.
          </Link>{' '}
          Если потерял доступ к почте и резервным кодам, обратись к администратору.
        </p>
      </div>
    </div>
  )
}
